import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { apiBase, getXEscritorio, setApiBase, setXEscritorio } from '../api';
import { useApiHealth, useAuth, useBranding, useEscritoriosSU, useMe, usePode, useReconciliacaoResumo, useTheme } from '../hooks';
import { AvatarUsuario } from './AvatarUsuario';

export function TopNav() {
  const { logout } = useAuth();
  const { data: me } = useMe();
  // Badge da Reconciliação: contagem de alertas abertos (gap-detector do cron).
  const ehGestorRec = me?.papel === 'admin' || me?.papel === 'gerente';
  const { data: recResumo } = useReconciliacaoResumo(ehGestorRec);
  const { data: branding } = useBranding();
  const { tema, toggle } = useTheme();
  const pode = usePode();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  // Status da API (ex-selo flutuante "API online" — agora vive neste menu).
  const { data: apiOnline, isLoading: apiTestando } = useApiHealth();
  function mudarApiBase() {
    const novo = prompt('URL da API (vazio = default):', apiBase());
    if (novo !== null) {
      setApiBase(novo);
      location.reload();
    }
  }
  const layoutAtual = typeof localStorage !== 'undefined' ? localStorage.getItem('viewPref') : null;

  // Super usuário: seletor de escritório (impersonação). xEsc = tenant ativo (ou null).
  const ehSuper = !!me?.super_admin;
  const xEsc = getXEscritorio();
  const { data: escritorios } = useEscritoriosSU(ehSuper);
  function trocarEscritorio(id: string) {
    setXEscritorio(id || null);   // '' = volta ao próprio escritório
    location.reload();            // recarrega tudo com o novo tenant
  }
  // O logo é um <img> (sem header X-Escritorio); ao impersonar, leva o tenant no
  // ?escritorio=N (o middleware aceita p/ super) — senão cairia no tenant-casa.
  const logoSrc = `/api/branding/logo${xEsc ? `?escritorio=${encodeURIComponent(xEsc)}` : ''}`;


  // Item de nav: ícone em cima, rótulo centralizado embaixo (compacto).
  const linkClasse = ({ isActive }: { isActive: boolean }) =>
    `flex flex-col items-center justify-center gap-0.5 px-2 py-1 rounded-lg min-w-[3.5rem] text-[11.5px] leading-none font-medium transition-colors ${
      isActive
        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
    }`;
  const ico = 'text-[18px] leading-none';

  function fazerLogout() {
    logout();
    navigate('/login');
  }

  function definirLayout(modo: 'mobile' | 'desktop') {
    localStorage.setItem('viewPref', modo);
    location.reload();
  }

  return (
    <header className="bg-white/90 dark:bg-slate-900/90 shadow-sm border-b border-slate-200 dark:border-slate-800/80 backdrop-blur-md sticky top-0 z-30">
      <div className="max-w-screen-2xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {branding?.tem_logo !== false ? (
            <img
              src={logoSrc}
              alt={branding?.nome_exibicao ?? 'Logomarca do escritório'}
              className="h-8 w-auto"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <span
              className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white"
              style={{ background: branding?.cor_primaria || '#2563eb' }}
              title="Suba a logo do escritório em Administração"
            >
              {(branding?.nome_exibicao || 'E')[0].toUpperCase()}
            </span>
          )}
          {branding?.nome_exibicao && (
            <span className="font-display font-semibold text-sm text-slate-700 dark:text-slate-200 hidden md:inline-block">
              {branding.nome_exibicao}
            </span>
          )}
        </div>

        <nav className="flex items-center gap-0.5 font-sans">
          {me?.papel !== 'audiencista' && (
            <NavLink to="/semanal" className={linkClasse} title={me?.papel === 'correspondente' ? 'Prazos concluídos' : 'Semanal'}>
              <span className={ico}>{me?.papel === 'correspondente' ? '📋' : '📅'}</span>
              <span>{me?.papel === 'correspondente' ? 'Concluídos' : 'Semanal'}</span>
            </NavLink>
          )}
          {me?.papel !== 'audiencista' && me?.papel !== 'correspondente' && (
            <NavLink to="/mensal" className={linkClasse} title="Mensal">
              <span className={ico}>🗓</span><span>Mensal</span>
            </NavLink>
          )}
          {(me?.papel === 'admin' || me?.papel === 'gerente') && (
            <NavLink to="/reconciliacao" className={linkClasse} title="Possíveis prazos não capturados (DataJud × DJEN)">
              <span className={`relative ${ico}`}>🛡
                {!!recResumo?.abertos && (
                  <span className="absolute -top-1.5 -right-2.5 inline-flex items-center justify-center min-w-[1rem] h-[1rem] px-0.5 text-[9px] font-bold rounded-full bg-amber-500 text-white"
                    title={`${recResumo.abertos} alerta(s) aberto(s) para revisar`}>
                    {recResumo.abertos}
                  </span>
                )}
              </span>
              <span>Reconciliação</span>
            </NavLink>
          )}
          {me?.papel !== 'correspondente' && (
            <NavLink to="/audiencias" className={linkClasse} title="Audiências">
              <span className={ico}>⚖️</span><span>Audiências</span>
            </NavLink>
          )}
          {me?.papel !== 'audiencista' && me?.papel !== 'correspondente' && me?.papel !== 'testador' && (
            <NavLink to="/caixa" className={linkClasse} title="Minha caixa">
              <span className={ico}>📥</span><span>Caixa</span>
            </NavLink>
          )}
          {me?.papel === 'admin' && (
            <NavLink to="/clientes" className={linkClasse} title="Clientes">
              <span className={ico}>👥</span><span>Clientes</span>
            </NavLink>
          )}
          {me?.papel === 'admin' && (
            <NavLink to="/financeiro" className={linkClasse} title="Financeiro">
              <span className={ico}>💰</span><span>Financeiro</span>
            </NavLink>
          )}
          {(me?.papel === 'admin' || me?.papel === 'gerente') && (
            <NavLink to="/jurisprudencia" className={linkClasse} title="Base de jurisprudência (inclusão manual + busca)">
              <span className={ico}>📚</span><span>Jurisprud.</span>
            </NavLink>
          )}
          {pode('aba.custos') && (
            <NavLink to="/custos" className={linkClasse} title="Monitor de gasto da assinatura (consumo de IA por chamada → R$)">
              <span className={ico}>💸</span><span>Custos</span>
            </NavLink>
          )}
          {pode('aba.peticoes_iniciais') && (
            <NavLink to="/peticoes-iniciais" className={linkClasse} title="Petições Iniciais">
              <span className={ico}>📑</span><span>Iniciais</span>
            </NavLink>
          )}
          {(me?.papel === 'admin' || me?.papel === 'gerente') && (
            <NavLink to="/peticionar-modelo" className={linkClasse} title="Gerar petição a partir de um modelo .docx">
              <span className={ico}>✍️</span><span>Peticionar</span>
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {ehSuper && (escritorios?.length ?? 0) > 0 && (
            <select
              value={xEsc ?? ''}
              onChange={(e) => trocarEscritorio(e.target.value)}
              title="Super usuário: visualizar outro escritório"
              className={`text-xs rounded-lg border px-2 py-1.5 max-w-[11rem] focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                xEsc
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                  : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-slate-600 dark:text-slate-300'
              }`}
            >
              <option value="">🏢 Meu escritório</option>
              {escritorios!.map((esc) => (
                <option key={esc.id} value={String(esc.id)}>
                  👁 {esc.nome}{esc.ativo === 0 || esc.ativo === false ? ' (inativo)' : ''}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={toggle}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-300 transition"
            title="Alternar tema"
          >
            {tema === 'dark' ? '☀️' : '🌙'}
          </button>
          <div className="relative">
            <button
              onClick={() => setOpenMenu((v) => !v)}
              className="rounded-full ring-2 ring-slate-100 dark:ring-slate-800 shadow-sm"
              title={me?.nome ?? ''}
            >
              <AvatarUsuario userId={me?.id} nome={me?.nome ?? '?'}
                corEtiqueta={me?.cor_etiqueta} tamanho="w-8 h-8" texto="text-xs" />
            </button>
            {openMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-850 z-40 p-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-150 dark:border-slate-800 font-medium">
                  {me?.nome} <span className="text-slate-400 dark:text-slate-550 font-normal">({me?.papel})</span>
                </div>

                {/* Administração do escritório — gestor/gerente (logo, modelo, usuários) */}
                {me?.pode_gerir_usuarios && (
                  <NavLink
                    to="/administracao"
                    onClick={() => setOpenMenu(false)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-200 rounded-lg transition"
                  >
                    ⚙️ Administração
                  </NavLink>
                )}
                {/* Status da API (clique = trocar a URL — ex-selo flutuante) */}
                <button
                  onClick={mudarApiBase}
                  title="Clique para mudar a URL da API"
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-200 rounded-lg transition"
                >
                  {apiTestando ? '⚪ API testando…' : apiOnline ? '🟢 API online' : '🔴 API offline'}
                  <span className="block text-[10px] text-slate-400 dark:text-slate-500 truncate">
                    {apiBase() || window.location.origin}
                  </span>
                </button>
                {(me?.papel === 'admin' || me?.papel === 'gerente') && (
                  <NavLink
                    to="/seguranca"
                    onClick={() => setOpenMenu(false)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-200 rounded-lg transition"
                  >
                    🔐 Segurança (acessos)
                  </NavLink>
                )}
                {me?.super_admin && (
                  <NavLink
                    to="/admin/escritorios"
                    onClick={() => setOpenMenu(false)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-200 rounded-lg transition"
                  >
                    🏢 Escritórios (superadmin)
                  </NavLink>
                )}

                {/* Layout: opções explícitas Celular / Desktop */}
                <div className="px-3 pt-2 pb-1 text-[9px] font-bold uppercase tracking-wider text-slate-450 dark:text-slate-500 border-t border-slate-150 dark:border-slate-800">
                  Layout
                </div>
                <div className="flex gap-1 px-2 pb-2">
                  <button
                    onClick={() => definirLayout('mobile')}
                    className={`flex-1 text-xs py-1.5 rounded-lg transition font-medium ${
                      layoutAtual === 'mobile'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-650 dark:text-slate-350 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    📱 Celular
                  </button>
                  <button
                    onClick={() => definirLayout('desktop')}
                    className={`flex-1 text-xs py-1.5 rounded-lg transition font-medium ${
                      layoutAtual === 'desktop'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-650 dark:text-slate-350 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    🖥 Desktop
                  </button>
                </div>

                <button
                  onClick={() => { setOpenMenu(false); navigate('/trocar-senha'); }}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-200 border-t border-slate-150 dark:border-slate-800 rounded-lg transition"
                >
                  🔑 Trocar senha
                </button>
                <button
                  onClick={fazerLogout}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-red-600 dark:text-red-400 font-semibold rounded-lg transition"
                >
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Banner de impersonação — deixa claro que o SU está vendo outro escritório */}
      {ehSuper && xEsc && (
        <div className="bg-amber-500/15 border-t border-amber-400/40 text-amber-800 dark:text-amber-300">
          <div className="max-w-screen-2xl mx-auto px-4 py-1.5 flex items-center justify-between gap-3 text-xs">
            <span className="flex items-center gap-1.5 font-medium">
              <span>👁</span>
              Visualizando o escritório: <strong>{me?.escritorio?.nome ?? `#${xEsc}`}</strong>
              <span className="text-amber-700/70 dark:text-amber-400/70">(você é super usuário)</span>
            </span>
            <button
              onClick={() => trocarEscritorio('')}
              className="px-2.5 py-1 rounded-md bg-amber-500/20 hover:bg-amber-500/30 font-semibold whitespace-nowrap"
            >
              ↩ Voltar ao meu escritório
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
